
import React, { useMemo, useState, useEffect } from "react";
import "./PaymentDetails.css";
import Widgets from "../../../widgets";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getNurseRequestDetails } from "../../../modules/NursignCare/ManageRequest/manageRequestThunk";
import { generateConsumableQuotation, manualConsumablePayment } from "../../../modules/NursignCare/AdditonalConsumable/AddConsumableThunk";
import ToastPopup from "../../../widgets/Toast/toastPopup";
import strings from "../../../utils/strings";
import Icons from "../../../Assets/Icons";
import spinnerIcon from "../../../Assets/Icons/spinner-solid 1.png";
import IconFemale from "../../../Assets/Icons/venus-mars-solid 1.png";
import call from "../../../Assets/Icons/call.png";
import shields from "../../../Assets/Icons/shield-halved-solid 1.png";

// Format a number as Vietnamese Dong with comma thousand separators.
const formatVND = (value) => {
  const n = Math.round(Number(value) || 0);
  return `₫${new Intl.NumberFormat("en-US").format(n)}`;
};

// Numeric, greater-than-zero validation shared by the Quotation Amount and
// Manual Payment Amount inputs.
const isValidAmount = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return false;
  if (!/^\d+(\.\d{1,2})?$/.test(raw)) return false;
  return Number(raw) > 0;
};

const formatDisplayDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()}`;
};

const formatDOB = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${d.getDate().toString().padStart(2, "0")} ${months[d.getMonth()]}, ${d.getFullYear()}`;
};

const formatTransactionDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()}, ${d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}`;
};

const sessionTotal = (sess) =>
  (sess.additionalConsumables || []).reduce(
    (sum, c) => sum + parseFloat(c.consumable?.price || 0) * (c.quantity || 0),
    0
  );

const PaymentDetails = () => {
  const { t } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();
  const { request, session: selectedSession } = location.state || {};
  const dispatch = useDispatch();
  // Extract request details from Redux, handling different response shapes
  const rawRequestDetails = useSelector((state) => state.manageRequest?.requestDetails);
  const quotationGeneratedSessionIds = useSelector((state) => state.addConsumable?.quotationGeneratedSessionIds || {});
  const requestDetails = useMemo(
    () => rawRequestDetails?.data?.request || rawRequestDetails?.request || rawRequestDetails || {},
    [rawRequestDetails]
  );

  // Local UI state
  const [expandedSessions, setExpandedSessions] = useState({});
  const [apiError, setApiError] = useState(null);
  const [distanceKm, setDistanceKm] = useState("");
  const [localPaymentStatus, setLocalPaymentStatus] = useState(null);

  // Quotation generation (non-cash payments only)
  const [quotationAmount, setQuotationAmount] = useState("");
  const [isQuotationLoading, setIsQuotationLoading] = useState(false);
  const [localQuotationGenerated, setLocalQuotationGenerated] = useState(false);

  // Collect Cash flow
  const [showCollectCashModal, setShowCollectCashModal] = useState(false);
  const [isCashLoading, setIsCashLoading] = useState(false);

  // Abscond flow
  const [showAbscondModal, setShowAbscondModal] = useState(false);
  const [isAbscondLoading, setIsAbscondLoading] = useState(false);


  const loading = request && !requestDetails;
  // Fetch request details on mount with error handling
  useEffect(() => {
    if (request && request._id) {
      dispatch(getNurseRequestDetails({ id: request._id }))
        .unwrap()
        .catch((err) => {
          setApiError(err?.message || 'Failed to load request details');
        });
    }
  }, [dispatch, request]);

  // Sync distance when request details are loaded
  useEffect(() => {
    if (request?.distanceInKm !== undefined) {
      setDistanceKm(String(request.distanceInKm));
    } else if (requestDetails?.distanceInKm !== undefined) {
      setDistanceKm(String(requestDetails.distanceInKm));
    }
  }, [request, requestDetails]);

  const patient = requestDetails?.patient || {};
  const address = requestDetails?.address || {};
  const allSessions = useMemo(
    () => requestDetails?.sessions || (selectedSession ? [selectedSession] : []),
    [requestDetails, selectedSession]
  );

  const sessionRows = useMemo(() =>
    allSessions.map((sess, idx) => {
      const consumables = sess.additionalConsumables || [];
      const total = sessionTotal(sess);
      const items = consumables.map((c, i) => ({
        slNo: String(i + 1).padStart(2, "0"),
        itemNo: c.consumable?.code || "-",
        itemName: c.consumable?.itemName?.en || "-",
        quantity: c.quantity || 0,
        costPerItem: parseFloat(c.consumable?.price || 0),
        totalFee: parseFloat(c.consumable?.price || 0) * (c.quantity || 0),
      }));
      const canExpand = items.length > 0;
      return {
        _id: sess._id,
        date: formatDisplayDate(sess.date),
        rawDate: sess.date,
        status: sess.additionalConsumablePaymentStatus,
        transactionId: sess.additionalConsumableTransactionId,
        paymentMethod: sess.additionalConsumablePayment?.method || null,
        collectedBy: sess.additionalConsumablePayment?.collectedBy || null,
        collectedByDetails: sess.additionalConsumablePayment?.collectedByDetails || null,
        nurseDetails: Array.isArray(sess.nurse) ? sess.nurse[0] : sess.nurse || null,
        paidAt: sess.additionalConsumablePayment?.paidAt || null,
        items,
        total,
        canExpand,
        expanded: Boolean(expandedSessions[idx]),
      };
    }),
    [allSessions, expandedSessions]
  );

  const grandTotal = sessionRows.reduce((s, r) => s + r.total, 0);
  const grandPaid = sessionRows.reduce((s, r) => s + (r.status === "COMPLETED" ? r.total : 0), 0);
  const grandRemaining = grandTotal - grandPaid;

  const overallStatus = selectedSession?.additionalConsumablePaymentStatus || allSessions[0]?.additionalConsumablePaymentStatus || "OUTSTANDING";
  const effectiveStatus = localPaymentStatus ?? overallStatus;

  // Payment method comes from the request; anything other than CASH follows the quotation flow.
  const rawPaymentMethod =
    requestDetails?.paymentMethod ??
    request?.paymentMethod ??
    selectedSession?.paymentMethod ??
    requestDetails?.paymentMode ??
    request?.paymentMode ??
    null;
  const isCashPayment = String(rawPaymentMethod || "").toUpperCase() === "CASH";

  const sessionId = selectedSession?._id || allSessions?.[0]?._id;

  // Whether a quotation has already been generated for this session. The backend
  // reports this as additionalConsumablePrice.quotation on the session, which
  // survives page refresh; the Redux-persisted map below covers the moment right
  // after generating a quotation, before a refetch has landed.
  const backendQuotationValue =
    selectedSession?.additionalConsumablePrice?.quotation ??
    allSessions?.[0]?.additionalConsumablePrice?.quotation ??
    null;
  const quotationGenerated =
    localQuotationGenerated ||
    Boolean(backendQuotationValue) ||
    Boolean(sessionId && quotationGeneratedSessionIds[sessionId]);

  // Auto-populate the Quotation Amount input with the Total Consumables Amount,
  // without clobbering a value the CMD user has already started editing.
  useEffect(() => {
    if (!isCashPayment && !quotationGenerated && grandTotal > 0 && quotationAmount === "") {
      setQuotationAmount(String(grandTotal));
    }
  }, [isCashPayment, quotationGenerated, grandTotal, quotationAmount]);

  const refreshRequestDetails = () => {
    if (request?._id) {
      dispatch(getNurseRequestDetails({ id: request._id })).catch(() => {});
    }
  };

  const handleGenerateQuotation = async () => {
    if (!isValidAmount(quotationAmount)) {
      ToastPopup.error(t("invalidQuotationAmount"));
      return;
    }
    if (!sessionId) {
      ToastPopup.error(t("errorLoadingRequest"));
      return;
    }
    setIsQuotationLoading(true);
    try {
      await dispatch(generateConsumableQuotation({ id: sessionId, quotation: String(quotationAmount).trim() })).unwrap();
      ToastPopup.success(t("quotationGeneratedSuccess"));
      setLocalQuotationGenerated(true);
      refreshRequestDetails();
    } catch (err) {
      ToastPopup.error(err?.message || t("invalidQuotationAmount"));
    } finally {
      setIsQuotationLoading(false);
    }
  };

  const handleCollectCash = async () => {
    if (!sessionId) {
      ToastPopup.error(t("errorLoadingRequest"));
      return;
    }
    setIsCashLoading(true);
    try {
      await dispatch(manualConsumablePayment({ id: sessionId, type: "CASH", amount: String(grandTotal) })).unwrap();
      ToastPopup.success(t("cashPaymentRecordedSuccess"));
      setLocalPaymentStatus("COMPLETED");
      setShowCollectCashModal(false);
      refreshRequestDetails();
    } catch (err) {
      ToastPopup.error(err?.message || t("invalidPaymentAmount"));
    } finally {
      setIsCashLoading(false);
    }
  };

  const handleAbscond = async () => {
    if (!sessionId) {
      ToastPopup.error(t("errorLoadingRequest"));
      return;
    }
    setIsAbscondLoading(true);
    try {
      await dispatch(manualConsumablePayment({ id: sessionId, type: "ABSCONDED", amount: String(grandTotal) })).unwrap();
      ToastPopup.success(t("paymentMarkedAbscondedSuccess"));
      setLocalPaymentStatus("ABSCONDED");
      setShowAbscondModal(false);
      refreshRequestDetails();
    } catch (err) {
      ToastPopup.error(err?.message || t("invalidPaymentAmount"));
    } finally {
      setIsAbscondLoading(false);
    }
  };

  const openCollectCashModal = () => {
    setShowCollectCashModal(true);
  };

  const openAbscondModal = () => {
    setShowAbscondModal(true);
  };

  const toggleSession = (idx) => {
    if (!sessionRows[idx].canExpand) return;
    setExpandedSessions((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const statusLabel = (s) => {
    if (s === "OUTSTANDING") return t("Outstanding");
    if (s === "COMPLETED") return t("Paid");
    if (s === "ABSCONDED") return t("Absconded");
    return s || "-";
  };

 const renderAddressWithBreak = (address) => {
  if (!address) return "-";

  const parts = [
    address.street,
    address.city,
    address.district,
    address.ward,
    address.province,
  ].filter(Boolean);

  // Remove duplicate values
  const uniqueParts = [...new Set(parts)];

  return `${uniqueParts.join(", ")} - ${address.postalCode || ""}`;
};

  if (loading) {
    return (
      <div className="pd-loading" style={{ textAlign: "center", padding: "24px" }}>
        <img src={spinnerIcon} alt="loading" />
      </div>
    );
  }

  // Show API error if request details failed to load
  if (apiError) {
    return (
      <div className="pd-error" style={{ textAlign: "center", padding: "24px", color: "red" }}>
        <p>{t('errorLoadingRequest')}</p>
        <p>{apiError}</p>
      </div>
    );
  }
  return (
    <div className="payment-details-container">
      <Widgets.BreadCrumb
        breadcrumbs={["nursingCare", "additionalCostManagement", "paymentDetails"]}
        paths={[
          strings.navigation.nursingCareManageService,
          strings.navigation.additionalCostManagement,
          strings.navigation.paymentDetails,
        ]}
      />

      {/* Header */}
      <div className="pd-header">
        <h1 className="pd-title">{t("additionalBillingPaymentDetails")}</h1>
        <span className={`pd-status-badge ${effectiveStatus === "COMPLETED" ? "status-paid" : effectiveStatus === "ABSCONDED" ? "status-absconded" : "status-outstanding"}`}>
          {effectiveStatus === "OUTSTANDING" && <img src={spinnerIcon} alt="" className="pd-status-icon" />}
          {effectiveStatus === "COMPLETED" && <img src={Icons.checkSolid} alt="" className="pd-status-icon" />}
          {statusLabel(effectiveStatus)}
        </span>
      </div>

      {/* Patient Information Card */}
      <div className="pd-card">
        <div className="pd-card-header">
          <h2 className="pd-section-title">{t("patientInformation")}</h2>
        </div>

        <div className="pd-patient-content">
          <div className="pd-patient-left">
            <img
              src={patient.profilePhoto || Icons.noProfile}
              alt="Patient"
              className="pd-patient-avatar"
            />
            <div className="pd-hn-box">
              <div className="pd-hn-label">{t("hospitalNumber")}</div>
              <div className="pd-hn-value">
                {patient.hnNumber || "-"}
              </div>
            </div>
          </div>

          <div className="pd-patient-right">
            <h3 className="pd-patient-name">{patient.fullName || "-"}</h3>

            <div className="pd-info-grid">
              <div className="pd-info-item">
                <img src={IconFemale} alt="" className="pd-icon" />
                <span>{patient.gender ? t(patient.gender) : "-"}</span>
              </div>
              <div className="pd-info-item dates">
                <img src={Icons.CalenderIcon} alt="" className="pd-icon" />
                <span>{formatDOB(patient.dateOfBirth)}</span>
              </div>
              <div className="pd-info-item">
                <img src={call} alt="" className="pd-icon" />
                <span>{patient.countryCode ? `+${patient.countryCode} ${patient.phoneNumber}` : patient.phoneNumber || "-"}</span>
              </div>
              <div className="pd-info-item gmails">
                <img src={Icons.SmsIcon} alt="" className="pd-icon" />
                <span>{patient.email || "-"}</span>
              </div>
            </div>

            <div className="pd-info-item pd-insurance insurence">
              <img src={shields} alt="" className="pd-icon" />
              <span>{patient.insuranceCategory ? t(patient.insuranceCategory) : "-"}</span>
            </div>

            {/* Patient Condition + Distance by KM — side by side */}
            <div className="ard-condition-zone-row" style={{ marginTop: '2rem' }}>
              <div className="ard-condition-col">
                <p className="ard-field-label">{t("patientCondition")}</p>
                <div className="ard-readonly-field">{requestDetails?.patientCondition || request?.patientCondition || "-"}</div>
              </div>
              <div className="ard-zone-col">
                <p className="ard-field-label">
                  {t("noofkm") || "DistanKM"}
                  <span className="asterik">*</span>
                </p>
                <div className="ard-zone-value">{distanceKm ? `${distanceKm} km` : "-"}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="pd-address-zone-row">
          <div className="pd-address-section">
            <div className="pd-address-label">
              <img src={Icons.Location} alt="" className="pd-icon" />
              <span>{t("patientAddress")}</span>
            </div>
            <p className="pd-address-text">
              {renderAddressWithBreak(address)}
            </p>
          </div>
        </div>
      </div>

      {/* Sessions Breakdown Card */}
      <div className="pd-card">
        <div className="pd-card-header">
          <h2 className="pd-section-title">{t("sessionsBreakdown")}</h2>
        </div>

        <div className="pd-sessions-content">
          <table className="pd-sessions-table">
            <colgroup>
              <col className="session-date" />
              <col className="items-used" />
              <col className="total-fee" />
            </colgroup>
            <thead>
              <tr>
                <th>{t("sessionDate")}</th>
                <th>{t("itemsUsed")}</th>
                <th>{t("totalFee")}</th>
              </tr>
            </thead>
            <tbody>
              {sessionRows.length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: "center", padding: "16px", color: "#888" }}>{t("noData")}</td></tr>
              ) : sessionRows.map((session, index) => (
                <React.Fragment key={session._id || index}>
                  <tr className="pd-session-row">
                    <td>{session.date}</td>
                    <td>
                      {session.canExpand ? (
                        <button
                          type="button"
                          className="pd-collapse-link"
                          onClick={() => toggleSession(index)}
                          aria-expanded={session.expanded}
                        >
                          {session.expanded ? t("clickToCollapse") : t("clickToExpand")}
                        </button>
                      ) : (
                        <span className="pd-collapse-link pd-collapse-link-disabled" aria-disabled="true">
                          {t("availableAfterSessionDate")}
                        </span>
                      )}
                    </td>
                    <td className="pd-fee">{formatVND(session.total)}</td>
                  </tr>

                  {session.expanded && (
                    <tr className="pd-session-details-row">
                      <td colSpan="3">
                        <table className="pd-session-detail-table">
                          <colgroup>
                            <col className="slno" />
                            <col className="itemno" />
                            <col className="qty" />
                            <col className="cost" />
                            <col className="total" />
                          </colgroup>
                          <thead>
                            <tr>
                              <th>{t("slNo")}</th>
                              <th>{t("itemNo")}</th>
                              <th>{t("quantity")}</th>
                              <th>{t("costPerItem")}</th>
                              <th>{t("totalFee")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {session.items.length > 0 ? (
                              <>
                                {session.items.map((item, itemIndex) => (
                                  <tr key={itemIndex} className="pd-detail-row">
                                    <td>{item.slNo}</td>
                                    <td>{item.itemNo}</td>
                                    <td>{item.quantity}</td>
                                    <td>{formatVND(item.costPerItem)}</td>
                                    <td>{formatVND(item.totalFee)}</td>
                                  </tr>
                                ))}
                                <tr className="pd-subtotal-row">
                                  <td colSpan="4">{t("total")}</td>
                                  <td>{formatVND(session.total)}</td>
                                </tr>
                              </>
                            ) : (
                              <tr className="pd-no-items-row">
                                <td colSpan="5">{t("noItemsUsed")}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment Details & Transaction History Card */}
      <div className="pd-card">
        <div className="pd-card-header">
          <h2 className="pd-section-title">{t("paymentDetailsAndTransactionHistory")}</h2>
        </div>

        <div className="pd-payment-content">
          {effectiveStatus === "OUTSTANDING" && (
            <table className="pd-payment-summary-table">
              <thead>
                <tr>
                  <th>{t("totalAmount")}</th>
                  <th>{t("amountPaid")}</th>
                  <th>{t("remainingBalance")}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="pd-amount">{formatVND(grandTotal)}</td>
                  <td className="pd-amount">{formatVND(grandPaid)}</td>
                  <td className="pd-amount pd-balance">{formatVND(grandRemaining)}</td>
                </tr>
              </tbody>
            </table>
          )}

          {/* CASH: status only, no quotation UI, no manual payment actions — homecare staff collects payment on the mobile app. */}
          {/* Non-cash + still outstanding + quotation not yet generated: editable Quotation Amount + Generate Quotation button. */}
          {!isCashPayment && effectiveStatus === "OUTSTANDING" && !quotationGenerated && (
            <div className="pd-quotation-section">
              <div className="pd-quotation-row" style={{ alignItems: "flex-start" }}>
                <div>
                  <label className="pd-field-label" htmlFor="pd-quotation-amount">{t("quotationAmount")}</label>
                  <input
                    id="pd-quotation-amount"
                    type="text"
                    inputMode="decimal"
                    className="pd-quotation-input"
                    value={quotationAmount}
                    onChange={(e) => setQuotationAmount(e.target.value)}
                  />
                </div>
                <div>
                  <label className="pd-field-label" aria-hidden="true" style={{ visibility: "hidden" }}>&nbsp;</label>
                  <button
                    className="pd-pay-complete-btn"
                    onClick={handleGenerateQuotation}
                    disabled={isQuotationLoading}
                    style={{ flexShrink: 0, height: 42 }}
                  >
                    {isQuotationLoading ? t("processing") : t("generateQuotation")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Non-cash + still outstanding + quotation generated: Collect Cash / Abscond, shown immediately without waiting on status. */}
          {!isCashPayment && effectiveStatus === "OUTSTANDING" && quotationGenerated && (
            <div className="pd-payment-actions">
              <button className="pd-pay-complete-btn" onClick={openCollectCashModal}>
                {t("collectCash")}
              </button>
              <button className="pd-mark-absconding-btn" onClick={openAbscondModal}>
                {t("abscond")}
              </button>
            </div>
          )}

          {/* Transaction Table — shown once payment/absconding is completed (manually or via mobile online payment) */}
          {effectiveStatus !== "OUTSTANDING" && (
            <table className="pd-transaction-table">
              <thead>
                <tr>
                  <th>{t("date")}</th>
                  <th>{t("amountPaid")}</th>
                  <th>{t("paymentStatus")}</th>
                  <th>{t("transactionId")}</th>
                  <th>{t("collectedBy")}</th>
                </tr>
              </thead>
              <tbody>
                {sessionRows.filter((s) => s.status !== "OUTSTANDING").length === 0 ? (
                  <tr>
                    <td colSpan="5" className="pd-no-data">{t("noData")}</td>
                  </tr>
                ) : (
                  sessionRows
                    .filter((s) => s.status !== "OUTSTANDING")
                    .map((s, i) => {
                      const isCash = s.status === "COMPLETED" && s.paymentMethod === "CASH";
                      return (
                        <tr key={i}>
                          <td>{formatTransactionDate(s.rawDate)}</td>
                          <td>{s.status === "COMPLETED" ? formatVND(s.total) : "-"}</td>
                          <td>
                            <span className={`status-pill ${s.status === "COMPLETED" ? "status-paid" : "status-absconded"}`}>
                              {s.status === "COMPLETED"
                                ? (s.paymentMethod === "CASH"
                                    ? t("cashPayment")
                                    : s.paymentMethod === "ONLINE"
                                      ? t("onlinePayment")
                                      : statusLabel(s.status))
                                : statusLabel(s.status)}
                            </span>
                          </td>
                          <td>{s.transactionId || "-"}</td>
                          <td>
                            {(() => {
                              const cb = s.collectedBy;
                              if (!cb) return "-";
                              // cb is a string (raw ID like "671c..." or keyword like "NURSE"/"ONLINE")
                              // fall back to collectedByDetails if available
                              const details = typeof cb === "string" ? s.collectedByDetails : cb;
                              if (!details) {
                                if (cb === "NURSE") {
                                  // fallback to nurse array on the session
                                  const nd = s.nurseDetails;
                                  if (nd) {
                                    const fn = typeof nd.firstName === "object" ? nd.firstName.en || nd.firstName.vi || "" : nd.firstName || "";
                                    const ln = typeof nd.lastName === "object" ? nd.lastName.en || nd.lastName.vi || "" : nd.lastName || "";
                                    return `${ln} ${fn}`.trim() || t("nurse") || "Nurse";
                                  }
                                  return t("nurse") || "Nurse";
                                }
                                if (cb === "ONLINE") return t("online") || "Online";
                                return "-";
                              }
                              const firstName = typeof details.firstName === "object" ? details.firstName.en || details.firstName.vi || "" : details.firstName || "";
                              const lastName = typeof details.lastName === "object" ? details.lastName.en || details.lastName.vi || "" : details.lastName || "";
                              const fullName = `${lastName} ${firstName}`.trim() || `${firstName} ${lastName}`.trim() || details.name || "";
                              const empId = details.employeeId || details.employeeID || "";
                              if (fullName && empId) return `${fullName} (${empId})`;
                              return fullName || empId || "-";
                            })()}
                          </td>
                        </tr>
                      );
                    })
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Collect Cash confirmation modal */}
      {showCollectCashModal && (
        <div className="status-modal-overlay">
          <div className="status-modal open">
            <div className="status-modal-header">{t("confirmCashCollection")}</div>
            <div className="status-modal-sub">{t("confirmCashCollectionDesc")}</div>
            <div className="status-modal-actions">
              <button className="status-cancel-btn" onClick={() => setShowCollectCashModal(false)} disabled={isCashLoading}>
                {t("cancel")}
              </button>
              <button className="status-confirm-btn" onClick={handleCollectCash} disabled={isCashLoading}>
                {isCashLoading ? t("processing") : t("confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Abscond confirmation modal */}
      {showAbscondModal && (
        <div className="status-modal-overlay">
          <div className="status-modal open">
            <div className="status-modal-header">{t("markAsAbsconded")}</div>
            <div className="status-modal-sub">{t("abscondConfirmDesc")}</div>
            <div className="status-modal-actions">
              <button className="status-cancel-btn" onClick={() => setShowAbscondModal(false)} disabled={isAbscondLoading}>
                {t("cancel")}
              </button>
              <button className="status-confirm-btn" onClick={handleAbscond} disabled={isAbscondLoading}>
                {isAbscondLoading ? t("processing") : t("confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PaymentDetails;
